import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Radio as BroadcastIcon,
  Send as SendIcon,
  FilterList as FilterIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Description as TemplateIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import SmsEncodingNotice from './SmsEncodingNotice';
import { analyzeSmsEncoding } from '../utils/smsEncoding';

export const buildRecipientPreviewEndpoint = (recipientType, params) => {
  if (recipientType === 'first_timer') {
    return `${process.env.REACT_APP_API_URL}/api/follow-up/all?${params}`;
  }
  if (recipientType === 'new_convert') {
    return `${process.env.REACT_APP_API_URL}/api/new-converts?${params}`;
  }
  if (recipientType === 'harvest') {
    return `${process.env.REACT_APP_API_URL}/api/harvest/contacts?${params}`;
  }
  return '';
};

const BroadcastCenter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [broadcasts, setBroadcasts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [broadcastMessages, setBroadcastMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  
  const [broadcastData, setBroadcastData] = useState({
    name: '',
    template_id: '',
    message_text: '',
    recipient_type: 'first_timer',
    filter_criteria: {
      start_date: '',
      end_date: '',
      service_unit: '',
      status: '',
      needs_transportation: false
    },
    scheduled_at: '',
    is_immediate: true
  });
  const smsAnalysis = analyzeSmsEncoding(broadcastData.message_text);

  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Load broadcasts and templates on component mount
  useEffect(() => {
    loadBroadcasts();
    loadTemplates();
  }, []);

  const loadBroadcasts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/broadcasts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBroadcasts(data);
      } else {
        setError('Failed to load broadcasts');
      }
    } catch (error) {
      console.error('Error loading broadcasts:', error);
      setError('Failed to load broadcasts');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/templates`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        console.error('Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBroadcastData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBroadcastData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleTemplateSelect = (template) => {
    setBroadcastData(prev => ({
      ...prev,
      template_id: template.id,
      message_text: template.message_text
    }));
  };

  const handleViewBroadcast = async (broadcast) => {
    setSelectedBroadcast(broadcast);
    setShowViewDialog(true);
    
    // Fetch individual message status for this broadcast
    try {
      setLoadingMessages(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/broadcasts/${broadcast.id}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBroadcastMessages(data);
      } else {
        console.error('Failed to fetch broadcast messages');
        setBroadcastMessages([]);
      }
          } catch (error) {
        console.error('Error fetching broadcast messages:', error);
        setBroadcastMessages([]);
      } finally {
        setLoadingMessages(false);
      }
  };

  const handlePreviewRecipients = async () => {
    try {
      setPreviewLoading(true);
      
      // Build filter parameters
      const params = new URLSearchParams({
        recipient_type: broadcastData.recipient_type,
        limit: 100
      });
      
      if (broadcastData.filter_criteria.start_date) {
        params.append('startDate', broadcastData.filter_criteria.start_date);
      }
      
      if (broadcastData.filter_criteria.end_date) {
        params.append('endDate', broadcastData.filter_criteria.end_date);
      }
      

      
      if (broadcastData.filter_criteria.status) {
        params.append('status', broadcastData.filter_criteria.status);
      }

      const endpoint = buildRecipientPreviewEndpoint(broadcastData.recipient_type, params);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const recipientsList = data.data || data.firstTimers || [];
        
        // Don't filter recipients here - let the backend handle invalid phone numbers
        // This allows us to capture and display failed messages properly
        const validRecipients = recipientsList;
        
        setPreviewRecipients(validRecipients);
        setShowPreviewDialog(true);
      } else {
        setError('Failed to preview recipients');
      }
    } catch (error) {
      console.error('Error previewing recipients:', error);
      setError('Failed to preview recipients');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendBroadcast = async () => {
    try {
      if (!broadcastData.name.trim() || !broadcastData.message_text.trim()) {
        setError('Name and message text are required');
        return;
      }

      if (broadcastData.message_text.length > 320) {
        setError('Message cannot exceed 320 characters (2 SMS pages)');
        return;
      }

      if (previewRecipients.length === 0) {
        setError('No recipients found with the current filters');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      const broadcastPayload = {
        ...broadcastData,
        recipient_count: previewRecipients.length,
        recipients: previewRecipients.map(r => ({
          id: r.id,
          phone_number: r.phone_number,
          recipient_type: broadcastData.recipient_type
        }))
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(broadcastPayload)
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(`Broadcast sent successfully to ${previewRecipients.length} recipients!`);
        setShowComposeDialog(false);
        setShowPreviewDialog(false);
        loadBroadcasts();
        
        // Reset form
        setBroadcastData({
          name: '',
          template_id: '',
          message_text: '',
          recipient_type: 'first_timer',
          filter_criteria: {
            start_date: '',
            end_date: '',
            service_unit: '',
            status: '',
            needs_transportation: false
          },
          scheduled_at: '',
          is_immediate: true
        });
      } else {
        setError(result.error || 'Failed to send broadcast');
      }
    } catch (error) {
      console.error('Error sending broadcast:', error);
      setError('Failed to send broadcast');
    } finally {
      setLoading(false);
    }
  };

  const getBroadcastStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'in_progress': 'info',
      'completed': 'success',
      'failed': 'error',
      'cancelled': 'default'
    };
    return colors[status] || 'default';
  };

  const getBroadcastStatusText = (status) => {
    const statusMap = {
      'pending': 'Pending',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'failed': 'Failed',
      'cancelled': 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading && broadcasts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#DC2626', fontWeight: 600 }}>
          <BroadcastIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Broadcast Center
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowComposeDialog(true)}
          sx={{
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)'
            }
          }}
        >
          New Broadcast
        </Button>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Broadcasts List */}
      {isMobile ? (
        // Mobile Cards View
        <Grid container spacing={2}>
          {broadcasts.map((broadcast) => (
            <Grid item xs={12} key={broadcast.id}>
              <Card sx={{ 
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                      {broadcast.name}
                    </Typography>
                    <Chip 
                      label={getBroadcastStatusText(broadcast.status)}
                      color={getBroadcastStatusColor(broadcast.status)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                    {broadcast.message_text || 'No message content'}
                  </Typography>
                  
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        <PeopleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Recipients: {broadcast.recipient_count || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Created: {formatDate(broadcast.created_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton 
                    color="primary" 
                    onClick={() => handleViewBroadcast(broadcast)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} sx={{ background: '#ffffff' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recipients</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {broadcasts.map((broadcast) => (
                <TableRow key={broadcast.id} sx={{ '&:hover': { background: '#f9fafb' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{broadcast.name}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {broadcast.message_text || 'No message content'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={<PeopleIcon />}
                      label={broadcast.recipient_count || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getBroadcastStatusText(broadcast.status)}
                      color={getBroadcastStatusColor(broadcast.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {formatDate(broadcast.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                                      <IconButton 
                    color="primary" 
                    onClick={() => handleViewBroadcast(broadcast)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={broadcasts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* No Broadcasts State */}
      {!loading && broadcasts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <BroadcastIcon sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No broadcasts yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create your first broadcast to start sending bulk SMS messages
          </Typography>
        </Box>
      )}

      {/* Compose Broadcast Dialog */}
      <Dialog 
        open={showComposeDialog} 
        onClose={() => setShowComposeDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ color: '#DC2626', fontWeight: 600 }}>
            <BroadcastIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Compose Broadcast Message
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Broadcast Name"
                value={broadcastData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., August Welcome Message"
                sx={{ mb: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Recipient Type</InputLabel>
                <Select
                  value={broadcastData.recipient_type}
                  onChange={(e) => handleInputChange('recipient_type', e.target.value)}
                  label="Recipient Type"
                >
                                  <MenuItem value="first_timer">First Timers</MenuItem>
                <MenuItem value="new_convert">New Converts</MenuItem>
                <MenuItem value="harvest">Harvest Contacts</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Template Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Template (Optional)</InputLabel>
                <Select
                  value={broadcastData.template_id}
                  onChange={(e) => {
                    const template = templates.find(t => t.id === e.target.value);
                    if (template) {
                      handleTemplateSelect(template);
                    }
                  }}
                  label="Template (Optional)"
                >
                  <MenuItem value="">No Template</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} ({template.category})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Message Text */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message Text"
                value={broadcastData.message_text}
                onChange={(e) => handleInputChange('message_text', e.target.value)}
                placeholder="Type your broadcast message here... Use {{name}} for variables"
                helperText={`${broadcastData.message_text.length}/320 characters`}
                error={broadcastData.message_text.length > 320}
                sx={{ mb: 2 }}
              />
              <SmsEncodingNotice message={broadcastData.message_text} />
            </Grid>

            {/* Filter Criteria */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Filter Recipients
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={broadcastData.filter_criteria.start_date}
                onChange={(e) => handleInputChange('filter_criteria.start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={broadcastData.filter_criteria.end_date}
                onChange={(e) => handleInputChange('filter_criteria.end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            </Grid>



            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={broadcastData.filter_criteria.needs_transportation}
                    onChange={(e) => handleInputChange('filter_criteria.needs_transportation', e.target.checked)}
                  />
                }
                label="Needs Transportation"
                sx={{ mb: 2 }}
              />
            </Grid>

            {/* Preview and Send */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={handlePreviewRecipients}
                  disabled={previewLoading}
                  sx={{ borderColor: '#DC2626', color: '#DC2626' }}
                >
                  {previewLoading ? <CircularProgress size={20} /> : 'Preview Recipients'}
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSendBroadcast}
                  disabled={loading || smsAnalysis.hasEmoji || !broadcastData.name.trim() || !broadcastData.message_text.trim()}
                  sx={{
                    background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)'
                    }
                  }}
                >
                  {loading ? 'Sending...' : 'Send Broadcast'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowComposeDialog(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Recipients Dialog */}
      <Dialog 
        open={showPreviewDialog} 
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preview Recipients ({previewRecipients.length})
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These recipients will receive your broadcast message:
          </Typography>
          
          <List>
            {previewRecipients.map((recipient, index) => (
              <ListItem key={recipient.id} sx={{ borderBottom: '1px solid #e5e7eb' }}>
                <ListItemText
                  primary={`${recipient.first_name} ${recipient.last_name}`}
                  secondary={recipient.phone_number}
                />
                <ListItemSecondaryAction>
                  <Chip 
                    label={`#${index + 1}`}
                    size="small"
                    variant="outlined"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Broadcast Details Dialog */}
      <Dialog 
        open={showViewDialog} 
        onClose={() => setShowViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BroadcastIcon sx={{ color: '#DC2626' }} />
            Broadcast Details: {selectedBroadcast?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBroadcast && (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Name:</strong> {selectedBroadcast.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> 
                      <Chip 
                        label={getBroadcastStatusText(selectedBroadcast.status)}
                        color={getBroadcastStatusColor(selectedBroadcast.status)}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Recipients:</strong> {selectedBroadcast.recipient_count || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Created:</strong> {formatDate(selectedBroadcast.created_at)}
                    </Typography>
                  </Grid>
                  {selectedBroadcast.scheduled_at && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Scheduled:</strong> {formatDate(selectedBroadcast.scheduled_at)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedBroadcast.started_at && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Started:</strong> {formatDate(selectedBroadcast.started_at)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedBroadcast.completed_at && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Completed:</strong> {formatDate(selectedBroadcast.completed_at)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Message Content */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                  Message Content
                </Typography>
                <Paper sx={{ p: 2, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedBroadcast.message_text || 'No message content available'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Filter Criteria */}
              {selectedBroadcast.filter_criteria && Object.keys(selectedBroadcast.filter_criteria).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                    Filter Criteria
                  </Typography>
                  <Paper sx={{ p: 2, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                    <Grid container spacing={2}>
                      {selectedBroadcast.filter_criteria.start_date && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Start Date:</strong> {selectedBroadcast.filter_criteria.start_date}
                          </Typography>
                        </Grid>
                      )}
                      {selectedBroadcast.filter_criteria.end_date && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>End Date:</strong> {selectedBroadcast.filter_criteria.end_date}
                          </Typography>
                        </Grid>
                      )}
                      {selectedBroadcast.filter_criteria.status && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Status:</strong> {selectedBroadcast.filter_criteria.status}
                          </Typography>
                        </Grid>
                      )}
                      {selectedBroadcast.filter_criteria.needs_transportation && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Needs Transportation:</strong> Yes
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Template Information */}
              {selectedBroadcast.template_name && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                    Template Used
                  </Typography>
                  <Chip 
                    label={selectedBroadcast.template_name}
                    variant="outlined"
                    color="primary"
                  />
                </Grid>
              )}

              {/* Individual Message Status */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                  Message Delivery Status
                </Typography>
                
                {loadingMessages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : broadcastMessages.length > 0 ? (
                  <Paper sx={{ p: 2, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Individual message delivery status for {broadcastMessages.length} recipients:
                    </Typography>
                    <List dense>
                      {broadcastMessages.map((message, index) => (
                        <ListItem key={message.id} sx={{ borderBottom: '1px solid #e5e7eb' }}>
                          <ListItemText
                            primary={message.recipient_name || `Recipient ${index + 1}`}
                            secondary={`${message.recipient_phone} • ${message.status} • ${formatDate(message.sent_at)}`}
                          />
                          <Chip 
                            label={message.status}
                            color={message.status === 'delivered' ? 'success' : message.status === 'sent' ? 'info' : 'warning'}
                            size="small"
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No individual message records found for this broadcast.
                  </Typography>
                )}
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => {
                      setShowViewDialog(false);
                      // Navigate to Message History tab and filter by broadcast
                      const broadcastId = selectedBroadcast?.id;
                      if (broadcastId) {
                        // Store the broadcast filter in sessionStorage for MessageHistory to pick up
                        sessionStorage.setItem('filterByBroadcast', broadcastId.toString());
                        // Navigate to Message History tab
                        window.location.hash = '#message-history';
                      }
                    }}
                    sx={{ borderColor: '#DC2626', color: '#DC2626' }}
                  >
                    View Full Message History
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BroadcastCenter;
